import { EnvironmentConfig } from "../config/environment";
import * as Sentry from "@sentry/react";
import { trackApiRequest, trackApiResponseTime, trackApiError } from "./analytics";
import { normalizeApiPath } from "./pathNormalization";
import { getDistinctId } from "../analytics/posthog";
import { detectWebView } from "./webViewUtils";
import { currentPagePath, recordRequestOrigin } from "./businessRequestOrigin";
import { isDemo } from "../config/demo";
import { DEMO_READ_ONLY_CODE, isDemoSilentPath, notifyDemoReadOnly } from "../services/demoReadOnlyNotifier";

// Global auth manager to handle logout without circular dependencies
let globalLogoutHandler: (() => Promise<void>) | null = null;
let globalAuthToken: string | null = null;
let refreshPromise: Promise<RefreshOutcome> | null = null;

// Callback to persist refreshed tokens (set by AuthContext)
let onTokensRefreshed: ((accessToken: string, refreshToken?: string) => Promise<void>) | null = null;

const ACCESS_TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const REFRESH_LOCK_NAME = "gb-refresh";

/**
 * What a refresh came to. Only `rejected` ends the session: it is the API
 * saying the refresh token is no good. `transient` (a 5xx, a dropped
 * connection) says nothing about the session, so it is kept and the caller
 * gets the error, the way it would for any request the network lost. Signing
 * out on a 502 during a deploy is what used to throw away a technician's
 * unsent clock presses (PRD 108 §4.2).
 */
export type RefreshOutcome =
  | { kind: "ok"; token: string }
  | { kind: "rejected" }
  | { kind: "transient"; error: Error };

/** Thrown by apiFetch when a 401 could not be refreshed for a reason that is not the session's. */
export class TokenRefreshUnavailableError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null = null) {
    super(message);
    this.name = "TokenRefreshUnavailableError";
    this.status = status;
  }
}

// The API's own answers that the refresh token is finished: 401 for an
// unknown, used or retired token, 404 when its user is gone. 403 is kept for a
// gateway in front of it that refuses outright.
const REFRESH_REJECTED_STATUSES = new Set([401, 403, 404]);

export const setGlobalLogoutHandler = (logoutHandler: () => Promise<void>) => {
  globalLogoutHandler = logoutHandler;
};

export const setGlobalAuthToken = (token: string | null) => {
  globalAuthToken = token;
};

/** The access token the client is using now — for the one caller that cannot send a header (a WebSocket, PRD 91 W5). */
export const getGlobalAuthToken = (): string | null => globalAuthToken;

export const setOnTokensRefreshed = (
  callback: ((accessToken: string, refreshToken?: string) => Promise<void>) | null
) => {
  onTokensRefreshed = callback;
};

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Runs `task` holding the browser-wide refresh lock, so two tabs that hit a
 * 401 together refresh once between them. The API rotates the refresh token
 * and retires the old one, so the second tab's refresh with the token it read
 * first would be refused, and that refusal signs out every tab. Without Web
 * Locks the in-tab promise below is still the lock within this tab.
 */
async function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (locks && typeof locks.request === "function") {
    return await locks.request(REFRESH_LOCK_NAME, task);
  }
  return task();
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Concurrent calls in this tab share one refresh; tabs share the Web Lock.
 * `failedToken` is the access token the 401 was for.
 */
export async function attemptTokenRefresh(failedToken: string | null): Promise<RefreshOutcome> {
  if (!readStored(REFRESH_TOKEN_KEY)) return { kind: "rejected" };

  if (refreshPromise) return refreshPromise;

  refreshPromise = withRefreshLock(() => refreshHoldingLock(failedToken));
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function refreshHoldingLock(failedToken: string | null): Promise<RefreshOutcome> {
  // Read again now the lock is held: the tab that held it before may have
  // rotated the pair already. Its new access token is then the answer, and
  // the refresh token this tab read before waiting is already retired.
  const storedAccessToken = readStored(ACCESS_TOKEN_KEY);
  const refreshToken = readStored(REFRESH_TOKEN_KEY);
  if (!refreshToken) return { kind: "rejected" };

  if (failedToken && storedAccessToken && storedAccessToken !== failedToken) {
    return adoptTokens(storedAccessToken, refreshToken);
  }

  let response: Response;
  try {
    const baseUrl = EnvironmentConfig.getApiUrl();
    // Timed out like any request: a refresh that never answers would hold
    // the lock, and with it every tab's refresh.
    response = await fetchWithTimeout(
      `${baseUrl}/refresh_token`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      },
      DEFAULT_API_TIMEOUT_MS,
      { method: "POST", path: "/refresh_token" }
    );
  } catch {
    return transient("The session could not be renewed: the API did not answer.", null);
  }

  if (REFRESH_REJECTED_STATUSES.has(response.status)) return { kind: "rejected" };
  if (!response.ok) {
    return transient(`The session could not be renewed: the API answered ${response.status}.`, response.status);
  }

  let data: { access_token?: string; refresh_token?: string } | null;
  try {
    data = await response.json();
  } catch {
    return transient("The session could not be renewed: the API's answer was unreadable.", response.status);
  }

  const newAccessToken = data?.access_token;
  if (!newAccessToken) {
    return transient("The session could not be renewed: the API sent no token.", response.status);
  }
  return adoptTokens(newAccessToken, data?.refresh_token || undefined);
}

async function adoptTokens(accessToken: string, refreshToken?: string): Promise<RefreshOutcome> {
  globalAuthToken = accessToken;
  if (onTokensRefreshed) {
    try {
      await onTokensRefreshed(accessToken, refreshToken);
    } catch {
      // The token is in memory; persisting it is best effort.
    }
  }
  return { kind: "ok", token: accessToken };
}

function transient(message: string, status: number | null): RefreshOutcome {
  Sentry.addBreadcrumb({ category: "auth", message, level: "warning" });
  return { kind: "transient", error: new TokenRefreshUnavailableError(message, status) };
}

/**
 * Get the base API URL from environment configuration
 */
export const getApiBaseUrl = (): string => {
  return EnvironmentConfig.getApiUrl();
};

/**
 * Build a full API endpoint URL
 */
export const getApiUrl = (path: string): string => {
  const baseUrl = getApiBaseUrl();
  // Handle paths that already contain query parameters
  if (path.includes("?")) {
    const [pathPart, queryPart] = path.split("?");
    const normalizedPathFirstPart = pathPart.startsWith("/") ? pathPart : `/${pathPart}`;
    return `${baseUrl}${normalizedPathFirstPart}?${queryPart}`;
  }

  // Ensure path starts with "/" and baseUrl doesn"t end with "/"
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

/** A request with no answer gives up after this long, unless the call says otherwise. */
export const DEFAULT_API_TIMEOUT_MS = 30_000;

// A GET that fails on the network or with a 5xx is asked again this many
// times, after about these waits (each jittered so a recovering API is not
// hit by every tab in step). Only GETs: a write is never resent, since a
// POST that reached the server before the connection dropped would run twice.
const GET_RETRY_DELAYS_MS = [300, 1000];

export interface ApiRequestOptions {
  skipAuth?: boolean;
  /**
   * Milliseconds to wait for the response headers before giving up with an
   * ApiTimeoutError. Defaults to DEFAULT_API_TIMEOUT_MS; 0 or null waits as
   * long as the browser does. A multipart upload has no default timeout,
   * since a large file on a slow connection can rightly take longer.
   */
  timeoutMs?: number | null;
  /** False turns off the GET retry, for a caller that retries on its own schedule. */
  retry?: boolean;
}

export type ApiFetchOptions = RequestInit & ApiRequestOptions;

/** Thrown when a request had no answer within its timeout. */
export class ApiTimeoutError extends Error {
  constructor(method: string, path: string, timeoutMs: number) {
    super(`${method} ${path} had no answer within ${Math.round(timeoutMs / 1000)}s`);
    this.name = "ApiTimeoutError";
  }
}

// fetch() rejects with a TypeError when the request never got an answer. A
// timeout or the caller's own abort is not one, and is not retried.
const isNetworkFailure = (error: unknown): boolean => error instanceof TypeError;

const backoff = (attempt: number): Promise<void> => {
  const base = GET_RETRY_DELAYS_MS[attempt];
  const wait = base * (0.75 + Math.random() * 0.5);
  return new Promise((resolve) => setTimeout(resolve, wait));
};

/** One fetch, aborted after `timeoutMs` while still honouring the caller's own signal. */
async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number | null,
  describe: { method: string; path: string }
): Promise<Response> {
  if (!timeoutMs || timeoutMs <= 0 || typeof AbortController === "undefined") {
    return fetch(url, init);
  }

  const controller = new AbortController();
  const callerSignal = init.signal;
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else callerSignal.addEventListener("abort", () => controller.abort(callerSignal.reason), { once: true });
  }

  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (error) {
    if (timedOut) throw new ApiTimeoutError(describe.method, describe.path, timeoutMs);
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Perform a fetch request to the API with automatic 401 handling, a default
 * timeout, and a bounded retry for GETs that fail on the network or with a 5xx.
 * @param path The API endpoint path
 * @param options Fetch options (method, headers, body, etc.)
 * @returns The fetch response
 */
export const apiFetch = async (
  path: string,
  options: ApiFetchOptions & { _retryAfterRefresh?: boolean } = {}
): Promise<Response> => {
  const url = getApiUrl(path);
  const { skipAuth, _retryAfterRefresh, timeoutMs, retry, ...fetchOptions } = options;
  const method = (fetchOptions.method || "GET").toUpperCase();
  const isUpload = typeof FormData !== "undefined" && fetchOptions.body instanceof FormData;
  const effectiveTimeoutMs = timeoutMs !== undefined ? timeoutMs : isUpload ? null : DEFAULT_API_TIMEOUT_MS;
  const retries = method === "GET" && retry !== false ? GET_RETRY_DELAYS_MS.length : 0;

  // Add auth token if available and not already set and not skipping auth
  const headers = new Headers(fetchOptions.headers);
  if (globalAuthToken && !skipAuth && !headers.get("Authorization")) {
    headers.set("Authorization", `Bearer ${globalAuthToken}`);
  }

  // Let the API attribute server-side analytics events to the same PostHog
  // person as the client. This is what keeps the funnel from fragmenting
  // across the anonymous → identified boundary, and it works cross-origin
  // where the posthog_anon_id cookie does not.
  const distinctId = getDistinctId();
  if (distinctId && !headers.get("X-PostHog-Distinct-Id")) {
    headers.set("X-PostHog-Distinct-Id", distinctId);
  }
  if (!headers.get("X-Client-Platform")) {
    headers.set("X-Client-Platform", detectWebView().isWebView ? "mobile_webview" : "web");
  }

  // A JSON body needs a JSON content type or Rails parses NOTHING. fetch()
  // defaults a string body to `text/plain`, and Rails only runs its JSON
  // parser on a JSON mime type — so the body arrives, is never parsed, and
  // the controller sees empty params. It does not fail loudly: it fails as
  // "a firm name is required" on a request that plainly carried one.
  //
  // Set here rather than per-service because every caller that forgot has
  // exactly this bug (firmService forgot on all seven of its writes, which
  // is what broke Accountant HQ activation). businessApiFetch already does
  // this for the business helpers; this is the same rule one layer down, so
  // the two seams cannot drift again.
  //
  // FormData must set its own Content-Type: the browser appends the multipart
  // boundary, and overriding it with application/json makes the body
  // unparseable server-side.
  if (!headers.get("Content-Type") && fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  // Ids and capability tokens out of the span name, the metric labels and
  // the breadcrumb (pathNormalization): one series per route, not per record.
  const routePath = normalizeApiPath(path);

  return Sentry.startSpan(
    {
      op: "http.client",
      name: `${method} ${routePath}`,
    },
    async (span) => {
      const startTime = Date.now();
      let response: Response;
      let attempt = 0;
      for (;;) {
        try {
          response = await fetchWithTimeout(url, { ...fetchOptions, headers }, effectiveTimeoutMs, { method, path: routePath });
        } catch (error) {
          if (attempt < retries && isNetworkFailure(error) && !fetchOptions.signal?.aborted) {
            await backoff(attempt);
            attempt += 1;
            continue;
          }
          throw error;
        }
        if (attempt < retries && response.status >= 500 && !fetchOptions.signal?.aborted) {
          await backoff(attempt);
          attempt += 1;
          continue;
        }
        break;
      }
      const duration = Date.now() - startTime;

      // Record HTTP status on the span
      span.setAttribute("http.status_code", response.status);
      if (attempt > 0) span.setAttribute("http.retry_count", attempt);

      // Track API metrics
      trackApiRequest(method, routePath, response.status);
      trackApiResponseTime(method, routePath, duration);

      // Track errors
      if (!response.ok) {
        trackApiError(method, routePath, response.status);
        Sentry.addBreadcrumb({
          category: "http",
          message: `${method} ${routePath} → ${response.status}`,
          level: response.status >= 500 ? "error" : "warning",
        });
      }

      // The Live sample refuses every write (PRD 117). Read from a clone so
      // the caller still gets the body for its own error handling, which is
      // what keeps a drawer open with the visitor's input in it. Only looked
      // at in demo mode: everywhere else a 403 is untouched.
      if (response.status === 403 && isDemo()) {
        await reportDemoRefusal(response, method, path);
      }

      // Handle 401 Unauthorized — attempt refresh before logout
      if (response.status === 401 && !_retryAfterRefresh) {
        const sentToken = headers.get("Authorization")?.replace(/^Bearer\s+/i, "") || null;
        const outcome = await attemptTokenRefresh(sentToken);
        if (outcome.kind === "ok") {
          // Retry the original request with the new token. A caller that
          // named its token (the boot check sends the stored one) gets the
          // new one too: resent as it was, the retry 401s again and the
          // boot check signs out a session the refresh had just saved.
          const retryHeaders = new Headers(options.headers);
          if (retryHeaders.has("Authorization")) retryHeaders.set("Authorization", `Bearer ${outcome.token}`);
          return apiFetch(path, { ...options, headers: retryHeaders, _retryAfterRefresh: true });
        }

        // The API could not be asked (a deploy, a dropped signal): the
        // session may be perfectly good, so it is kept and the caller sees
        // the failure as it would any request the network lost.
        if (outcome.kind === "transient") throw outcome.error;

        // The refresh token was refused — sign out
        if (globalLogoutHandler) {
          try {
            await globalLogoutHandler();
          } catch {
            // Swallow error during logout
          }
        }
      }

      return response;
    }
  );
};

async function reportDemoRefusal(response: Response, method: string, path: string): Promise<void> {
  try {
    const body = await response.clone().json();
    if (body?.error?.code !== DEMO_READ_ONLY_CODE) return;
    if (isDemoSilentPath(method, path)) return;
    notifyDemoReadOnly();
  } catch {
    // Not JSON: not a demo refusal.
  }
}

// Convenience methods that match the mobile app pattern
export const apiGet = async (path: string, config?: ApiFetchOptions) =>
  apiFetch(path, { ...config, method: "GET" });

export const apiPost = async (path: string, data?: any, config?: ApiFetchOptions) =>
  apiFetch(path, {
    ...config,
    method: "POST",
    body: data ? JSON.stringify(data) : undefined,
  });

export const apiPut = async (path: string, data?: any, config?: ApiFetchOptions) =>
  apiFetch(path, {
    ...config,
    method: "PUT",
    body: data ? JSON.stringify(data) : undefined,
  });

export const apiPatch = async (path: string, data?: any, config?: ApiFetchOptions) =>
  apiFetch(path, {
    ...config,
    method: "PATCH",
    body: data ? JSON.stringify(data) : undefined,
  });

export const apiDelete = async (path: string, config?: ApiFetchOptions) =>
  apiFetch(path, { ...config, method: "DELETE" });

/**
 * Multipart, session-scoped. The business-scoped twin below has existed since
 * the invoice logo upload; this one arrived with client requests (PRD 63 §5.2),
 * where the person uploading a document may have no business at all.
 *
 * NO Content-Type set here on purpose: the browser appends the multipart
 * boundary, and overriding it makes the body unparseable server-side.
 */
export const apiPostForm = async (
  path: string,
  form: FormData,
  config?: ApiFetchOptions
) => apiFetch(path, { ...config, method: "POST", body: form });

// ============================================================
// Business-scoped API (Greenback Business, /api/v1/business/*)
// ============================================================
// Business tenant context rides in the `X-Business-Id` header (opaque bearer
// tokens carry no claims, so the server re-resolves membership per request).
// The active business id is set by BusinessContext, mirroring the
// setGlobalAuthToken pattern above. Non-business call sites are unaffected.

let globalBusinessId: string | null = null;

export const setGlobalBusinessId = (businessId: string | null) => {
  globalBusinessId = businessId;
};

export const getGlobalBusinessId = (): string | null => globalBusinessId;

/**
 * apiFetch that injects the `X-Business-Id` header for business-scoped
 * endpoints. Throws when no business is selected — callers should only reach
 * business-scoped endpoints from within an active business context.
 */
export const businessApiFetch = async (
  path: string,
  options: ApiFetchOptions = {}
): Promise<Response> => {
  const headers = new Headers(options.headers);
  if (!headers.get("X-Business-Id")) {
    if (!globalBusinessId) {
      throw new Error("No active business selected — business-scoped API calls require X-Business-Id");
    }
    headers.set("X-Business-Id", globalBusinessId);
  }
  // FormData must set its own Content-Type: the browser appends the multipart
  // boundary, and overriding it with application/json makes the body
  // unparseable server-side. The logo upload is the first multipart caller.
  if (!headers.get("Content-Type") && options.body && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  // Noted BEFORE the await: a refusal for a page the person has since left
  // must not be pinned on the page they moved to (businessRequestOrigin).
  const origin = { path: currentPagePath(), businessId: headers.get("X-Business-Id") };
  const response = await apiFetch(path, { ...options, headers });
  recordRequestOrigin(response, origin);
  return response;
};

export const businessApiGet = async (path: string, config?: ApiFetchOptions) =>
  businessApiFetch(path, { ...config, method: "GET" });

export const businessApiPost = async (path: string, data?: any, config?: ApiFetchOptions) =>
  businessApiFetch(path, {
    ...config,
    method: "POST",
    body: data ? JSON.stringify(data) : undefined,
  });

export const businessApiPut = async (path: string, data?: any, config?: ApiFetchOptions) =>
  businessApiFetch(path, {
    ...config,
    method: "PUT",
    body: data ? JSON.stringify(data) : undefined,
  });

export const businessApiPatch = async (path: string, data?: any, config?: ApiFetchOptions) =>
  businessApiFetch(path, {
    ...config,
    method: "PATCH",
    body: data ? JSON.stringify(data) : undefined,
  });

export const businessApiDelete = async (path: string, config?: ApiFetchOptions) =>
  businessApiFetch(path, { ...config, method: "DELETE" });

/** POST multipart/form-data (file uploads). Content-Type is left to the browser. */
export const businessApiPostForm = async (
  path: string,
  form: FormData,
  config?: ApiFetchOptions
) => businessApiFetch(path, { ...config, method: "POST", body: form });